import { useState } from 'react';
import Modal from '../Modal';

/**
 * Ajout personne — parcours opérationnel (sans création de compte Admin par défaut).
 * L'accès Clickorico s'active ensuite depuis la fiche, onglet dédié.
 */
export default function CreatePersonWizard({ isOpen, onClose, onSubmit, loading }) {
  const [step, setStep] = useState(1);
  const [isIntervenant, setIsIntervenant] = useState(null);
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    fonctionMetier: '',
    telephone: '',
    email: '',
  });

  const reset = () => {
    setStep(1);
    setIsIntervenant(null);
    setForm({ prenom: '', nom: '', fonctionMetier: '', telephone: '', email: '' });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canProceedStep2 = form.prenom.trim() && form.nom.trim();
  const canSubmit = canProceedStep2 && isIntervenant !== null;

  const handleSubmitIdentity = (e) => {
    e.preventDefault();
    if (!canProceedStep2) return;
    setStep(2);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      hasAdminAccess: false,
      isIntervenant: Boolean(isIntervenant),
      prenom: form.prenom.trim(),
      nom: form.nom.trim(),
      fonctionMetier: form.fonctionMetier.trim() || null,
      telephone: form.telephone.trim() || null,
      email: form.email.trim() || null,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Ajouter une personne" maxWidth="max-w-lg">
      <div className="mb-4 flex gap-2">
        {[1, 2].map((n) => (
          <div
            key={n}
            className={`h-1 flex-1 rounded-full ${step >= n ? 'bg-primary-600' : 'bg-gray-200'}`}
          />
        ))}
      </div>

      {step === 1 && (
        <form onSubmit={handleSubmitIdentity} className="space-y-4">
          <p className="text-sm text-gray-600">
            Ajoutez une personne de votre équipe. L&apos;accès à l&apos;administration Clickorico pourra
            être activé plus tard si nécessaire.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Prénom *</label>
              <input
                required
                value={form.prenom}
                onChange={(e) => setForm((p) => ({ ...p, prenom: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Nom *</label>
              <input
                required
                value={form.nom}
                onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Fonction / métier</label>
            <input
              value={form.fonctionMetier}
              onChange={(e) => setForm((p) => ({ ...p, fonctionMetier: e.target.value }))}
              placeholder="Ex. Esthéticienne, Technicien piscine…"
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Téléphone</label>
            <input
              type="tel"
              value={form.telephone}
              onChange={(e) => setForm((p) => ({ ...p, telephone: e.target.value }))}
              placeholder="Optionnel"
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="Optionnel"
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            />
          </div>
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={!canProceedStep2}
              className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </form>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-gray-800">
            Cette personne réalise-t-elle des prestations ?
          </p>
          <p className="text-xs text-gray-500">
            Si oui, vous pourrez ensuite lui attribuer des prestations, un planning et des absences.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setIsIntervenant(true)}
              className={`flex-1 rounded-xl border px-4 py-4 text-sm font-medium transition ${
                isIntervenant === true
                  ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              Oui
            </button>
            <button
              type="button"
              onClick={() => setIsIntervenant(false)}
              className={`flex-1 rounded-xl border px-4 py-4 text-sm font-medium transition ${
                isIntervenant === false
                  ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              Non
            </button>
          </div>
          <div className="flex justify-between pt-2">
            <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-600">
              Retour
            </button>
            <button
              type="button"
              disabled={loading || !canSubmit}
              onClick={handleSubmit}
              className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {loading ? 'Création…' : 'Ajouter'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
